import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { EntryDetail } from '../components/journal/EntryDetail';
import { listJournalEntries } from '../graphql/journal';
import { JournalEntry } from '../types/journal';

const client = generateClient();

export function JournalDetailPage() {
  const { entryId } = useParams<{ entryId: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function loadEntry() {
      // Check if entry was passed via router state
      const stateEntry = (location.state as { entry?: JournalEntry } | null)?.entry;
      if (stateEntry && stateEntry.entryId === entryId) {
        setEntry(stateEntry);
        setIsLoading(false);
        return;
      }

      // Fall back to fetching the full list and filtering client-side
      if (!user?.userId || !entryId) {
        setIsLoading(false);
        setNotFound(true);
        return;
      }

      try {
        const res = await client.graphql({
          query: listJournalEntries,
          variables: { userId: user.userId },
        });

        const entries = (res as { data: { listJournalEntries: JournalEntry[] } }).data
          .listJournalEntries;

        const found = entries.find((e) => e.entryId === entryId);
        if (found) {
          setEntry(found);
        } else {
          setNotFound(true);
        }
      } catch {
        setError('Failed to load journal entry. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    loadEntry();
  }, [user?.userId, entryId, location.state]);

  useEffect(() => {
    if (!isLoading) {
      // Focus the page heading for error/not-found states
      if (headingRef.current) {
        headingRef.current.focus();
        return;
      }
      // For success state, focus the h2 rendered by EntryDetail
      const heading = containerRef.current?.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }
  }, [isLoading, entry, error, notFound]);

  if (isLoading) {
    return (
      <div className="page">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <h2 ref={headingRef} tabIndex={-1}>
          Journal Entry
        </h2>
        <ErrorMessage message={error} />
        <Link to="/journal" className="cta-button" style={{ marginTop: '1rem', display: 'inline-block' }}>
          Back to Journal
        </Link>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="page">
        <h2 ref={headingRef} tabIndex={-1}>
          Entry Not Found
        </h2>
        <p>This journal entry could not be found. It may have been deleted or does not exist.</p>
        <Link to="/journal" className="cta-button" style={{ marginTop: '1rem', display: 'inline-block' }}>
          Back to Journal
        </Link>
      </div>
    );
  }

  if (!entry) return null;

  return (
    <div className="page journal-detail-page" ref={containerRef}>
      <EntryDetail entry={entry} />
      <Link to="/journal" className="cta-button" style={{ marginTop: '1rem', display: 'inline-block' }}>
        Back to Journal
      </Link>
    </div>
  );
}
